import { useMemo } from 'react';
import {
  CallStatus,
  IncludesAnalysis,
  IncludesHydration,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallIntelQueries, CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';

const RECENT_CALLS_ANALYSIS = [
  IncludesAnalysis.CALL_CATEGORY,
  IncludesAnalysis.CALL_SUMMARY,
  IncludesAnalysis.CALL_SENTIMENT,
  IncludesAnalysis.FOLLOWUP_TASKS,
  IncludesAnalysis.CALL_APPOINTMENT_TYPE,
];

const RECENT_CALLS_HYDRATION = [IncludesHydration.HYDRATE_PERSON, IncludesHydration.HYDRATE_OFFICE_USER];

const TRENDS_ANALYSIS = [IncludesAnalysis.CALL_CATEGORY, IncludesAnalysis.CALL_SENTIMENT];

interface UsePatientInsightsRequestsProps {
  dateRange: { start: string; end: string };
  personId: string;
  locationId: string;
  showTrends: boolean;
}

export const usePatientInsightsRequests = ({
  dateRange,
  personId,
  locationId,
  showTrends,
}: UsePatientInsightsRequestsProps) => {
  const alert = useAlert();
  const { t } = useTranslation('callIntelInsights');

  const recentCallsRequest = useMemo(
    () => ({
      locationId,
      personId,
      includesAnalysis: RECENT_CALLS_ANALYSIS,
      includesHydration: RECENT_CALLS_HYDRATION,
      limit: 1,
    }),
    [locationId, personId]
  );

  const callOverviewFilter = useMemo(
    () => ({
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: { dateRange },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: { locations: [locationId] },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: { callStatus: [CallStatus.CALL_STATUS_COMPLETE] },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID]: { personId: [personId] },
    }),
    [locationId, personId, dateRange]
  );

  const callTrendsRequest = useMemo(
    () => ({
      filter: callOverviewFilter,
      includesAnalysis: TRENDS_ANALYSIS,
    }),
    [callOverviewFilter]
  );

  const { data: lastCallDetail, isLoading: isLastCallLoading } = CallIntelQueries.useRecentCallsQuery({
    request: recentCallsRequest,
    requestKeysToOmitFromQueryKey: ['includesAnalysis', 'includesHydration', 'limit', 'personId', 'locationId'],
    options: {
      enabled: !!(locationId && personId),
      select: (data) => data.calls?.[0],
      onError: () => {
        alert.error(t("Couldn't fetch recent call. Please try again."));
      },
    },
  });

  const { data: callTrends, isLoading: isCallTrendsLoading } = CallIntelQueries.useGetCallsOverviewQuery({
    request: callTrendsRequest,
    options: {
      enabled: showTrends,
      onError: () => {
        alert.error(t("Couldn't fetch overview details. Please try again."));
      },
    },
  });

  return { lastCallDetail, isLastCallLoading, callTrends, isCallTrendsLoading };
};
